<script setup>
import { useI18n } from 'vue-i18n';
import { CheckCheck, Inbox } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import NotificationItem from './NotificationItem.vue';

const props = defineProps({
    notifications: {
        type: Array,
        required: true
    },
    isLoading: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['markAsRead', 'markAllAsRead', 'notification-clicked']);

const { t } = useI18n();

const hasUnread = () => props.notifications.some(n => !n.read_at);

const handleNotificationClick = (notification) => {
    if (!notification.read_at) {
        emit('markAsRead', notification.id);
    }
    emit('notification-clicked', notification);
};

const handleMarkAllAsRead = () => {
    emit('markAllAsRead');
};
</script>

<template>
    <div class="flex flex-col">
        <!-- Header with Mark All as Read -->
        <div 
            v-if="notifications.length > 0 && hasUnread()" 
            class="flex items-center justify-end px-3 py-2 border-b border-border/50"
        >
            <Button 
                variant="ghost" 
                size="sm" 
                class="h-7 text-xs text-muted-foreground hover:text-foreground gap-1.5"
                @click="handleMarkAllAsRead"
            >
                <CheckCheck class="w-3.5 h-3.5" />
                {{ t('notifications.mark_all_read') }}
            </Button>
        </div>

        <!-- Notifications List with native scroll -->
        <div class="overflow-y-auto max-h-[350px] scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
            <div v-if="isLoading" class="flex items-center justify-center py-8">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>

            <div v-else-if="notifications.length === 0" class="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div class="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                    <Inbox class="w-6 h-6 text-muted-foreground" />
                </div>
                <p class="text-sm text-muted-foreground">
                    {{ t('notifications.empty') }}
                </p>
            </div>

            <div v-else class="p-2 flex flex-col gap-1">
                <NotificationItem
                    v-for="notification in notifications"
                    :key="notification.id"
                    :notification="notification"
                    @click="handleNotificationClick"
                />
            </div>
        </div>
    </div>
</template>
